// src/views/hrms/holidays/EmployeeWeeklyOffPicker.jsx
//
// NEW feature layered on top of the original company-level weekly off.
//
// Architecture:
//   Company default  →  PUT /hrms-settings/weekly-offs        (HolidayList page)
//   Per-employee     →  PUT /employees/:id/weekly-offs         (this page)
//
// Employee app logic (read side — same DB):
//   if employee.weeklyOffDays is set  → use that
//   else                              → fall back to hrms-settings weeklyOffDays
//
// Bulk Apply here ONLY calls PUT /employees/:id/weekly-offs for each visible
// employee. It never touches /hrms-settings/weekly-offs, so the HolidayList
// calendar and company default are completely unaffected.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import {
  getEmployees,
  getAllEmployeeWeeklyOffs,
  updateEmployeeWeeklyOffs,
  getWeeklyOffs,                // to show company default as reference
} from "services/hrms.api";
import Loading from "components/common/Loading";

/* ─── constants ─────────────────────────────────────────────── */

const DAYS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

/* ─── helpers ───────────────────────────────────────────────── */

function normalizeWeeklyOffs(arr) {
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map((x) => Number(x)))]
    .filter((x) => Number.isInteger(x) && x >= 0 && x <= 6)
    .sort((a, b) => a - b);
}

function getInitials(name = "") {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

/* ─── EmployeeRow ────────────────────────────────────────────── */

function EmployeeRow({ emp, offDays, companyDefault, onSave }) {
  const [localDays, setLocalDays] = useState(offDays);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  // sync when parent updates (e.g. after bulk apply)
  const prevRef = useRef(JSON.stringify(offDays));
  useEffect(() => {
    const next = JSON.stringify(offDays);
    if (prevRef.current !== next) {
      setLocalDays(offDays);
      prevRef.current = next;
      setSaved(false);
    }
  }, [offDays]);

  const toggle = (v) => {
    setSaved(false);
    setLocalDays((prev) => {
      const s = new Set(prev);
      s.has(v) ? s.delete(v) : s.add(v);
      return [...s].sort((a, b) => a - b);
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(emp.dbId, localDays);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      // toast shown by parent
    } finally {
      setSaving(false);
    }
  };

  // Reset to company default (not Sun+Sat hardcoded — uses live company setting)
  const handleReset = () => {
    setLocalDays(companyDefault);
    setSaved(false);
  };

  const isDirty   = JSON.stringify(localDays) !== JSON.stringify(offDays);
  const isDefault = JSON.stringify(localDays) === JSON.stringify(companyDefault);

  const offLabel =
    localDays.length === 0
      ? "No days off"
      : localDays.map((d) => DAYS[d].label).join(", ") + " off";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
      {/* top row */}
      <div className="flex items-center justify-between gap-3">
        {/* left — avatar + info */}
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-600 dark:bg-brand-500/20 dark:text-brand-300">
            {getInitials(emp.name)}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-800 dark:text-white">
              {emp.name}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-white/50">
              <span>{emp.code || emp.dbId}</span>
              {emp.department  && <><span>·</span><span>{emp.department}</span></>}
              {emp.designation && <><span>·</span><span className="truncate">{emp.designation}</span></>}
            </div>
          </div>
        </div>

        {/* right — label + save */}
        <div className="flex flex-shrink-0 items-center gap-3">
          <span className="hidden text-xs text-gray-400 dark:text-white/40 sm:block">
            {offLabel}
          </span>

          {saved && !isDirty ? (
            <span className="rounded-lg bg-green-100 px-3 py-1.5 text-xs font-medium text-green-700 dark:bg-green-500/20 dark:text-green-300">
              Saved ✓
            </span>
          ) : (
            <button
              disabled={saving || !isDirty}
              onClick={handleSave}
              className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-4 py-1.5 text-xs text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      {/* day pills */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {DAYS.map((d) => {
          const active = localDays.includes(d.value);
          return (
            <button
              key={d.value}
              type="button"
              onClick={() => toggle(d.value)}
              className={[
                "min-w-[52px] rounded-lg border px-3 py-1.5 text-xs font-medium transition",
                active
                  ? "border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white/80 dark:hover:bg-white/5",
              ].join(" ")}
            >
              {d.label}
            </button>
          );
        })}

        {/* Reset to company default — only shows when different */}
        {!isDefault && (
          <button
            type="button"
            onClick={handleReset}
            className="text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline dark:text-white/40 dark:hover:text-white/60"
          >
            Reset to company default ({companyDefault.map((d) => DAYS[d].label).join(" & ")})
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── BulkPanel ──────────────────────────────────────────────── */
// Applies chosen days to all visible employees via PUT /employees/:id/weekly-offs.
// Does NOT touch PUT /hrms-settings/weekly-offs (company default stays unchanged).

function BulkPanel({ onApply, visibleCount, companyDefault }) {
  const [bulkDays, setBulkDays] = useState([]);
  const [applying, setApplying] = useState(false);

  const toggle = (v) =>
    setBulkDays((prev) => {
      const s = new Set(prev);
      s.has(v) ? s.delete(v) : s.add(v);
      return [...s].sort((a, b) => a - b);
    });

  const handleApply = async () => {
    if (bulkDays.length === 0) return;
    setApplying(true);
    await onApply(bulkDays);
    setApplying(false);
    setBulkDays([]);
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-800 dark:text-white">
            Bulk Apply Weekly Off
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-white/60">
            Select days then apply to all{" "}
            <span className="font-medium text-gray-700 dark:text-white/80">
              {visibleCount}
            </span>{" "}
            visible employee{visibleCount !== 1 ? "s" : ""}.
            <span className="ml-1 text-gray-400 dark:text-white/40">
              Company default: {companyDefault.map((d) => DAYS[d].label).join(" & ")}
            </span>
          </div>
        </div>

        <button
          disabled={bulkDays.length === 0 || applying}
          onClick={handleApply}
          className="self-start rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-4 py-2 text-sm text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {applying
            ? "Applying…"
            : `Apply to ${visibleCount} employee${visibleCount !== 1 ? "s" : ""}`}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {DAYS.map((d) => {
          const active = bulkDays.includes(d.value);
          return (
            <button
              key={d.value}
              type="button"
              onClick={() => toggle(d.value)}
              className={[
                "min-w-[52px] rounded-lg border px-3 py-2 text-xs font-medium transition",
                active
                  ? "border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white/80 dark:hover:bg-white/5",
              ].join(" ")}
            >
              {d.label}
            </button>
          );
        })}

        {/* Quick-select company default */}
        {bulkDays.length > 0 && (
          <button
            type="button"
            onClick={() => setBulkDays(companyDefault)}
            className="text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline dark:text-white/40 dark:hover:text-white/60"
          >
            Use company default
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */

export default function EmployeeWeeklyOffPicker() {
  const [employees, setEmployees]         = useState([]);
  const [weekendMap, setWeekendMap]       = useState({}); // { dbId → number[] }
  const [companyDefault, setCompanyDefault] = useState([0, 6]); // from hrms-settings
  const [loading, setLoading]             = useState(true);
  const [err, setErr]                     = useState("");
  const [search, setSearch]               = useState("");
  const [searchQ, setSearchQ]             = useState("");
  const [toast, setToast]                 = useState(null);

  /* debounce search */
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [search]);

  /* load */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");

        const [empRes, offRes, companyRes] = await Promise.all([
          getEmployees(),
          getAllEmployeeWeeklyOffs().catch(() => ({ data: [] })),
          // Fetch company default so we can show it as reference & use for "Reset"
          getWeeklyOffs().catch(() => ({ data: { weeklyOffDays: [0, 6] } })),
        ]);

        // normalize company default
        const cDays = normalizeWeeklyOffs(companyRes?.data?.weeklyOffDays);
        setCompanyDefault(cDays.length ? cDays : [0, 6]);

        // normalize employees
        const raw = Array.isArray(empRes?.data?.items)
          ? empRes.data.items
          : Array.isArray(empRes?.data)
          ? empRes.data
          : [];

        const normalized = raw.map((e) => {
          const fullName =
            `${e.firstName || ""} ${e.lastName || ""}`.trim() ||
            e.username || e.email || e.code || e._id;
          return { ...e, dbId: e._id, name: fullName };
        });

        setEmployees(normalized);

        // Build weekendMap:
        //   Start with company default for every employee.
        //   Then override with their specific setting if one exists.
        //   This mirrors what the employee app does at read time.
        const defaultDays = cDays.length ? cDays : [0, 6];
        const map = {};
        normalized.forEach((emp) => { map[emp.dbId] = defaultDays; });

        const offList = Array.isArray(offRes?.data) ? offRes.data : [];
        offList.forEach(({ employeeId, weeklyOffDays }) => {
          if (map[employeeId] === undefined) return;
          const cleaned = normalizeWeeklyOffs(weeklyOffDays);
          // null/empty from backend means "use company default"
          map[employeeId] = cleaned.length ? cleaned : defaultDays;
        });

        setWeekendMap(map);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to load employees");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* save one employee — PUT /employees/:id/weekly-offs only */
  const handleSave = async (dbId, days) => {
    const cleaned = normalizeWeeklyOffs(days);
    const res     = await updateEmployeeWeeklyOffs(dbId, cleaned);
    const saved   = normalizeWeeklyOffs(res?.data?.weeklyOffDays);

    setWeekendMap((prev) => ({
      ...prev,
      [dbId]: saved.length ? saved : cleaned,
    }));

    showToast("Weekly off saved!", true);
  };

  /* bulk save — PUT /employees/:id/weekly-offs for each visible employee.
     Company default (/hrms-settings/weekly-offs) is NEVER touched here. */
  const handleBulkApply = async (days) => {
    const cleaned = normalizeWeeklyOffs(days);

    const results = await Promise.allSettled(
      filtered.map((emp) => updateEmployeeWeeklyOffs(emp.dbId, cleaned))
    );

    const successIds = [];
    results.forEach((r, i) => {
      if (r.status === "fulfilled") successIds.push(filtered[i].dbId);
    });

    if (successIds.length) {
      setWeekendMap((prev) => {
        const next = { ...prev };
        successIds.forEach((id) => (next[id] = cleaned));
        return next;
      });
    }

    const failed = results.length - successIds.length;
    showToast(
      failed > 0
        ? `Saved ${successIds.length}, ${failed} failed`
        : `Updated ${successIds.length} employee${successIds.length !== 1 ? "s" : ""}`,
      failed === 0
    );
  };

  const showToast = (msg, ok) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  /* filtered list */
  const filtered = useMemo(() => {
    if (!searchQ) return employees;
    return employees.filter((e) =>
      [e.name, e.code, e.department, e.designation]
        .map((v) => (v || "").toLowerCase())
        .some((v) => v.includes(searchQ))
    );
  }, [employees, searchQ]);

  /* ── render ── */
  if (loading) {
    return <div className="p-6"><Loading variant="dots" fullscreen /></div>;
  }

  return (
    <div className="relative space-y-5 p-4 md:p-6">

      {/* Header */}
      <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold dark:text-white">
            Employee Weekly Off
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-white/60">
            Override weekly-off days per employee. Employees without an override
            automatically use the company default (
            <span className="font-medium text-gray-700 dark:text-white/70">
              {companyDefault.map((d) => DAYS[d].label).join(" & ")}
            </span>
            ).
          </p>
        </div>
        <div className="mt-1 text-xs text-gray-400 dark:text-white/40 md:mt-2">
          {employees.length} employee{employees.length !== 1 ? "s" : ""} total
        </div>
      </div>

      {/* Error banner */}
      {err && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {err}
        </div>
      )}

      {/* Bulk apply */}
      <BulkPanel
        onApply={handleBulkApply}
        visibleCount={filtered.length}
        companyDefault={companyDefault}
      />

      {/* Search */}
      <div className="flex justify-end">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, code, department…"
            className="w-72 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm dark:border-gray-700 dark:bg-navy-800 dark:text-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {searchQ && (
        <div className="text-xs text-gray-500 dark:text-white/50">
          Showing {filtered.length} of {employees.length} employees
        </div>
      )}

      {/* Employee rows */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white py-10 text-center text-sm text-gray-500 dark:border-white/10 dark:bg-navy-800 dark:text-white/50">
            No employees found.
          </div>
        ) : (
          filtered.map((emp) => (
            <EmployeeRow
              key={emp.dbId}
              emp={emp}
              offDays={weekendMap[emp.dbId] ?? companyDefault}
              companyDefault={companyDefault}
              onSave={handleSave}
            />
          ))
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 rounded-xl px-4 py-3 text-sm font-medium shadow-lg transition-all ${
            toast.ok ? "bg-green-600 text-white" : "bg-red-600 text-white"
          }`}
        >
          {toast.ok ? "✓ " : "✕ "}{toast.msg}
        </div>
      )}
    </div>
  );
}
